import { useMemo, useCallback, useEffect, useState, useRef } from 'react'
import { useQueryParams, useLocation } from '@fs/zion-router'
import { getMostRecentFieldValue, createHighlight, useSlsStateContext } from '@fs/zion-sls'
import { getImageStuff } from '../../../../shared/services/sls/slsService'
import generateCitation from '../../../../shared/utils/generateCitation'
import { stripGarbageCharacters } from '../../../../shared/utils/stringNormalization'
import { useR2StateContext } from '../../../providers/r2Provider/R2Context'
import { useSharedBrowserStateContext } from '../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { errorCollector } from '../../../../shared/utils/utils'

const getApidFromImageArk = ({ imageArk, thumbnails }) => {
  const thumbnail = thumbnails.find((t) => t.ark === imageArk)
  return thumbnail?.id
}

const getMetadataProperty = (properties, name) => properties?.find((p) => p.name === name)?.value

const EMPTY_TOKENS = []

// A token's rect is "x,y,w,h", or several of those separated by spaces when the token
// spans more than one box (a name split across a line or a column, say).
const parseTokenRects = (rect) =>
  (rect?.trim().split(/\s+/) || [])
    .map((part) => part.split(',').map((pos) => Number.parseInt(pos, 10)))
    .filter((parts) => parts.length === 4 && parts.every(Number.isFinite))

// The textless M and E tokens that carry the rest of the B token at `idx` onto later lines.
const continuationsOf = (tokens, idx) => {
  const continuations = []
  for (let next = idx + 1; next < tokens.length; next++) {
    const token = tokens[next]
    if (token.text || (token.split !== 'M' && token.split !== 'E')) break
    continuations.push(token)
    if (token.split === 'E') break
  }
  return continuations
}

/**
 * The boxes of each word, given the boxes of the words together. One box per word when
 * the counts match, and every box for a lone word. Otherwise nothing says where one word
 * ends, so the boxes are shared out by character position, as if laid end to end. That
 * gives no two words the same box, so the image can call out each one.
 * @param {string[]} words
 * @param {Array<{x: number, y: number, width: number, height: number}>} rects
 * @returns {Array<Array>} the boxes of each word
 */
const boxesForWords = (words, rects) => {
  if (!rects.length) return words.map(() => [])
  if (words.length === 1) return [rects]
  if (rects.length === words.length) return rects.map((rect) => [rect])
  const length = words.join(' ').length
  const total = rects.reduce((sum, rect) => sum + rect.width, 0)
  let start = 0
  return words.map((word) => {
    const from = (start / length) * total
    const to = ((start + word.length) / length) * total
    start += word.length + 1
    let offset = 0
    return rects.reduce((boxes, rect) => {
      const lo = Math.max(from, offset)
      const hi = Math.min(to, offset + rect.width)
      if (hi > lo) boxes.push({ ...rect, x: rect.x + lo - offset, width: hi - lo })
      offset += rect.width
      return boxes
    }, [])
  })
}

// A token's `orig` holds the words the handwriting recognition read, in Unicode math
// script (𝓗𝓪𝓻𝓻𝓲𝓮𝓽). Its boxes belong to those words. `text` is the corrected reading,
// which can add or move words: "James Stedman" over a box where only "James" is written.
const origWordsOf = (token) => (token.orig ? token.orig.normalize('NFKC').split(' ').filter(Boolean) : null)
const wordKey = (word) => word.toLowerCase().replaceAll(/[^\p{L}\p{N}]/gu, '')

/**
 * The boxes of each word of a token whose corrected text has a different number of words
 * than its `orig`. A word that is in `orig` gets the box of that orig word. When there is
 * one box per word, the boxes after the orig words' go, in order, to the words that are
 * not in `orig`. Any other word gets no box, because nothing says where it is written.
 * @returns {Array<Array>} the boxes of each word
 */
const boxesByOrig = (words, origWords, rects) => {
  const taken = new Set()
  const origIndexes = words.map((word) => {
    const index = origWords.findIndex(
      (orig, idx) => !taken.has(idx) && wordKey(orig) && wordKey(orig) === wordKey(word)
    )
    if (index >= 0) taken.add(index)
    return index
  })
  if (rects.length === words.length && words.length > origWords.length) {
    const spare = rects.slice(origWords.length)
    return words.map((word, idx) =>
      origIndexes[idx] >= 0 ? [rects[origIndexes[idx]]] : [spare.shift()].filter(Boolean)
    )
  }
  const origBoxes = boxesForWords(origWords, rects)
  return words.map((word, idx) => (origIndexes[idx] >= 0 ? origBoxes[origIndexes[idx]] : []))
}

/**
 * The boxes of each word of a token, for the full text hover.
 * @returns {Array<Array>} the boxes of each word
 */
const wordBoxesOf = (token, words, continuations, rectsOf) => {
  // A word split over two lines comes as a B token holding the whole word or phrase,
  // then an E token with no text holding the boxes of the second half. The boxes are
  // all drawn for the B token's words, and the E token's empty word gets none.
  const parts = token.split === 'E' && !token.text ? [] : [token, ...continuations]
  const origWordCount = (part) => part.orig?.split(' ').filter(Boolean).length || 0
  // Each half's own words get that half's boxes, when its words can be told apart.
  if (parts.length > 1 && parts.reduce((sum, part) => sum + origWordCount(part), 0) === words.length) {
    return parts.flatMap((part, idx) => {
      const before = parts.slice(0, idx).reduce((sum, p) => sum + origWordCount(p), 0)
      return boxesForWords(words.slice(before, before + origWordCount(part)), rectsOf(part))
    })
  }
  const rects = parts.flatMap((part) => rectsOf(part))
  const origWords = parts.length === 1 && origWordsOf(token)
  if (origWords?.length && origWords.length !== words.length) return boxesByOrig(words, origWords, rects)
  return boxesForWords(words, rects)
}

const useDownloadOverlayData = (setAllHighlights, setWordHighlights) => {
  const {
    query: { view: queryView },
  } = useQueryParams()
  const state = useSlsStateContext()
  const sharedBrowserState = useSharedBrowserStateContext()
  const location = useLocation()
  const r2State = useR2StateContext()
  // The image data and the ark it belongs to are one state value, so they always change in the same render. React 17
  // does not batch two setState calls made after an await, and a render with the new transcript but the old ark made
  // callers read a transcript that was not marked ready yet.
  const [loadedImageStuff, setLoadedImageStuff] = useState({ imageStuff: null, transcriptSourceArk: null })
  const { imageStuff, transcriptSourceArk } = loadedImageStuff
  const [imageStuffError, setImageStuffError] = useState(null)
  const recordsCache = useRef(new Map())
  const imageArk = location.pathname.split('/').pop()

  /**
   * Split a stuff token's text into one field per word, and collect two sets of boxes for
   * those words: the keyword highlights, which give the whole token's box to its first
   * word only, and the word boxes for the full text hover, which give each word its own.
   * They stay apart so the hover never changes how keywords are highlighted.
   * @returns {Array} one field per word
   */
  const splitByTextSpaces = useCallback(
    (token, allHighlightsTemp, wordHighlightsTemp, continuations = EMPTY_TOKENS) => {
      const fields = []
      const words = token.text?.split(' ') || []

      if (words.length === 0 && token.redacted) {
        fields.push({
          rects: [token?.rect],
          fieldValues: [{ id: token.id, origValue: { text: '' } }],
          id: token.id,
          redacted: true,
        })
        return fields
      }

      const imageHeight = imageStuff?.stuff?.pages?.[0]?.pageHeight
      const imageWidth = imageStuff?.stuff?.pages?.[0]?.pageWidth
      const toRatios = (parts) =>
        imageHeight && imageWidth ? parts.map((pos, idx) => pos / (idx % 2 === 0 ? imageWidth : imageHeight)) : parts
      const pushHighlight = (highlights, { x, y, width, height }, fieldId) =>
        highlights.push(
          createHighlight(
            { id: state.currentImageArk },
            { x1: x, y1: y, x2: x + width, y2: y + height, fieldId, imageId: state.currentImageArk },
            highlights.length
          )
        )

      // Keyword highlights: one box only, for the first word.
      const keywordRect = token.rect ? token.rect.split(',').map((pos) => Number.parseInt(pos, 10)) : []
      if (keywordRect.length === 4 && words.length) {
        const [x, y, width, height] = toRatios(keywordRect)
        pushHighlight(allHighlightsTemp, { x, y, width, height }, words.length === 1 ? token.id : `${token.id}_0`)
      }

      if (wordHighlightsTemp) {
        const rectsOf = (part) =>
          parseTokenRects(part.rect)
            .map(toRatios)
            .map(([x, y, width, height]) => ({ x, y, width, height }))
        const wordBoxes = wordBoxesOf(token, words, continuations, rectsOf)
        words.forEach((w, idx) => {
          const fieldId = words.length === 1 ? token.id : `${token.id}_${idx}`
          wordBoxes[idx].forEach((box) => pushHighlight(wordHighlightsTemp, box, fieldId))
        })
      }

      words.forEach((w, idx) => {
        fields.push({
          rects: [token?.rect],
          fieldValues: [{ id: token.id, origValue: { text: w } }],
          id: words.length === 1 ? token.id : `${token.id}_${idx}`, // Unique ID for each word split
          ...(token.redacted && { redacted: true }),
        })
      })
      return fields
    },
    [imageStuff, state.currentImageArk]
  )

  /**
   * Create sls-like field objects from stuff data.
   * @returns {Array} field objects with field values and rects.
   */
  const generateFieldsFromImageStuff = useCallback(() => {
    const allHighlightsTemp = []
    const wordHighlightsTemp = setWordHighlights ? [] : null

    const tokens = (imageStuff?.stuff?.regions ?? [])
      .flatMap((region) => (region.lines ?? []).flatMap((line) => line.tokens))
      .filter(Boolean)
    const fieldsTemp = tokens.flatMap((token, idx) =>
      splitByTextSpaces(
        token,
        allHighlightsTemp,
        wordHighlightsTemp,
        token.split === 'B' ? continuationsOf(tokens, idx) : EMPTY_TOKENS
      )
    )
    if (setAllHighlights) {
      setAllHighlights(allHighlightsTemp)
    }
    if (setWordHighlights) {
      setWordHighlights(wordHighlightsTemp)
    }
    return fieldsTemp
  }, [imageStuff?.stuff?.regions, setAllHighlights, setWordHighlights, splitByTextSpaces])

  const fields = useMemo(() => generateFieldsFromImageStuff(), [generateFieldsFromImageStuff])

  const plainTextTranscript = useMemo(() => {
    return fields.map((field) => stripGarbageCharacters(getMostRecentFieldValue(field))).join(' ')
  }, [fields])

  // Ready when the fetch for the current ark completes, even if it has no fields (an empty transcript is still "ready")
  const isTranscriptReady = useMemo(
    () => transcriptSourceArk === state.currentImageArk,
    [transcriptSourceArk, state.currentImageArk]
  )

  let citation = ''

  const {
    groupData: { creators: creator, custodians: custodian, dgsNumber: imageGroupNumber, coverages },
  } = r2State

  const imageCount = sharedBrowserState.unifiedApidList?.length
  const imageApid = getApidFromImageArk({
    imageArk,
    thumbnails: sharedBrowserState?.thumbnails,
  })
  const imageIndex = imageApid
    ? sharedBrowserState.unifiedApidList?.indexOf(imageApid)
    : sharedBrowserState.focusedIndex

  const place = coverages?.find((c) => c?.place)?.place

  const metadataProperties = imageStuff?.stuff?.metadata?.properties
  const recordPlace = getMetadataProperty(metadataProperties, 'RECORD_PLACE')
  const recordDate = getMetadataProperty(metadataProperties, 'RECORD_DATE')
  const imageClassification = getMetadataProperty(metadataProperties, 'IMAGE_CLASSIFICATION')
  const recordLanguage = getMetadataProperty(metadataProperties, 'LANGUAGE')

  citation = generateCitation({
    creator,
    custodian,
    imageCount,
    imageIndex,
    location,
    queryView,
    imageGroupNumber,
    place,
  })

  useEffect(() => {
    // Set to true when the ark changes again (effect cleanup), so a slow fetch for an earlier image can't replace the
    // current image's data when it finishes
    let isStale = false

    const fetchTranscript = async () => {
      // Reset transcript source immediately when effect fires (when ark changes)
      setLoadedImageStuff((previous) => ({ ...previous, transcriptSourceArk: null }))

      if (state.currentImageArk && queryView === 'fullText') {
        setImageStuffError(null)
        if (recordsCache.current.has(state.currentImageArk)) {
          // Cached data is ready immediately
          setLoadedImageStuff({
            imageStuff: recordsCache.current.get(state.currentImageArk),
            transcriptSourceArk: state.currentImageArk,
          })
          return
        }

        try {
          const result = await getImageStuff(state.currentImageArk)
          // The cache is keyed by the fetch's own ark, so store the result even if the user has moved on
          recordsCache.current.set(state.currentImageArk, result)
          if (isStale) return
          setLoadedImageStuff({ imageStuff: result, transcriptSourceArk: state.currentImageArk }) // Ready for this ark
        } catch (e) {
          if (isStale) return
          setImageStuffError(true)
          errorCollector(e, state.currentImageArk, { codeId: 41 })
        }
      }
    }
    fetchTranscript()
    return () => {
      isStale = true
    }
  }, [state.currentImageArk, queryView])

  return {
    citation,
    plainTextTranscript,
    fields,
    imageStuffError,
    isTranscriptReady,
    recordPlace,
    recordDate,
    imageClassification,
    recordLanguage,
  }
}

export default useDownloadOverlayData
